import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Send, Flag, Ban, Users, LogOut, Loader2, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { displayAuthorName, displayAuthorPicture } from "@/lib/anonAuthor";

const REPORT_REASONS = ["harassment", "hate", "sexual", "spam", "privacy", "illegal", "impersonation", "abuse", "other"];

export default function ChatDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const scrollRef = useRef(null);
  const [reportMsg, setReportMsg] = useState(null);
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [minorBlocked, setMinorBlocked] = useState(false);

  const loadChat = async () => {
    try {
      const { data } = await api.get(`/chat/chats/${id}`);
      setChat(data);
    } catch (e) {
      if (e.response?.status === 403) { setMinorBlocked(true); return; }
      if (e.response?.status === 404) { setNotFound(true); return; }
    }
  };
  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/chat/chats/${id}/messages`, { params: { limit: 100 } });
      setMessages(data.items || []);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (e) {
      if (e.response?.status === 403) setMinorBlocked(true);
    }
  };

  useEffect(() => {
    if (!loading && !user) { nav("/auth?mode=signin"); return; }
    loadChat();
    loadMessages();
    const iv = setInterval(() => { loadChat(); loadMessages(); }, 10000);
    return () => clearInterval(iv);
  // eslint-disable-next-line
  }, [id, user, loading]);

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/chat/chats/${id}/messages`, { text: text.trim() });
      setText("");
      await loadMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    } finally { setBusy(false); }
  };

  const leaveGroup = async () => {
    if (!window.confirm(t("chat_group_leave_confirm"))) return;
    try {
      await api.post(`/chat/groups/${id}/leave`);
      nav("/chats");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    }
  };
  const blockPeer = async (uid) => {
    try { await api.post("/chat/blocks", { user_id: uid }); toast.success(t("chat_block_user")); }
    catch (e) { toast.error(e.response?.data?.detail || t("chat_error_send")); }
  };
  const submitReport = async () => {
    if (!reportMsg) return;
    try {
      await api.post(`/chat/messages/${reportMsg.message_id}/report`, { reason, details });
      toast.success(t("chat_report_message"));
      setReportMsg(null); setDetails(""); setReason("spam");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("chat_error_send"));
    }
  };

  if (loading) return null;
  if (!user) return null;
  if (minorBlocked) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <div className="max-w-2xl mx-auto p-4 pt-8">
          <BackButton />
          <div data-testid="chat-detail-adult-only" className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 text-amber-900 p-5 flex items-center gap-2">
            <ShieldAlert size={18} /> {t("chat_adult_only")}
          </div>
        </div>
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50">
        <AppHeader />
        <div className="max-w-2xl mx-auto p-4 pt-8">
          <BackButton />
          <div className="mt-4 text-sm text-slate-500 italic">{t("content_removed")}</div>
        </div>
      </div>
    );
  }
  if (!chat) return null;

  const isPending = chat.my_role === "pending";
  const canWrite = !isPending && chat.active;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <AppHeader />
      <div className="max-w-3xl w-full mx-auto p-4 flex-1 flex flex-col min-h-[70vh]">
        <BackButton to="/chats" />
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex-1 flex flex-col mt-2">
          {/* Header */}
          <div className="p-3 border-b border-slate-100 flex items-center gap-3">
            {chat.kind === "group" ? (
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                <Users size={18} />
              </div>
            ) : chat.peer?.picture ? (
              <img src={chat.peer.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-slate-200" />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate" data-testid="chat-detail-title">
                {chat.kind === "group" ? chat.title : chat.peer?.name || "—"}
              </div>
              {chat.kind === "group" && (
                <div className="text-xs text-slate-500 truncate">
                  {(chat.members || []).map((m) => m.name).slice(0, 3).join(", ")}
                  {(chat.members || []).length > 3 ? "…" : ""}
                </div>
              )}
              {chat.origin_type === "marketplace_listing" && chat.origin_id && (
                <Link to={`/marketplace/${chat.origin_id}`} className="text-xs text-emerald-700 underline">Marketplace</Link>
              )}
            </div>
            <div className="flex gap-1">
              {chat.kind === "1to1" && chat.peer?.user_id && (
                <Button
                  data-testid="chat-detail-block-peer"
                  size="sm"
                  variant="outline"
                  onClick={() => blockPeer(chat.peer.user_id)}
                  className="rounded-full"
                >
                  <Ban size={13} className="mr-1" /> {t("chat_block_user")}
                </Button>
              )}
              {chat.kind === "group" && !isPending && (
                <Button
                  data-testid="chat-detail-leave-group"
                  size="sm"
                  variant="outline"
                  onClick={leaveGroup}
                  className="rounded-full"
                >
                  <LogOut size={13} className="mr-1" /> {t("chat_group_leave")}
                </Button>
              )}
            </div>
          </div>

          {isPending && (
            <div className="p-3 bg-amber-50 border-b border-amber-200 text-xs text-amber-900" data-testid="chat-detail-pending-banner">
              {t("chat_pending_invite_banner")}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 min-h-[300px] overflow-y-auto p-3 space-y-2" data-testid="chat-detail-messages">
            {messages.length === 0 && !isPending && (
              <div className="text-xs text-slate-500 italic text-center py-4">{t("no_messages_write_first")}</div>
            )}
            {messages.map((m) => {
              const mine = m.author_id === user.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-emerald-100" : "bg-slate-100"}`}>
                    <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-600 mb-0.5">
                      <span>{displayAuthorName(m, t)}</span>
                      <span className="text-slate-400 font-normal">
                        {new Date(m.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {!mine && (
                        <button
                          data-testid={`chat-msg-report-${m.message_id}`}
                          onClick={() => setReportMsg(m)}
                          className="ml-1 text-red-600 hover:underline"
                          title={t("chat_report_message")}
                        >
                          <Flag size={10} />
                        </button>
                      )}
                    </div>
                    <div className="whitespace-pre-line">{m.text}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Composer */}
          {canWrite && (
            <div className="p-2 border-t border-slate-100 flex gap-2">
              <Input
                data-testid="chat-detail-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={t("chat_composer_placeholder")}
                className="rounded-full flex-1"
              />
              <Button
                data-testid="chat-detail-send"
                onClick={send}
                disabled={busy || !text.trim()}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white hover:text-white"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Report dialog */}
      <Dialog open={!!reportMsg} onOpenChange={(o) => { if (!o) setReportMsg(null); }}>
        <DialogContent data-testid="chat-report-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("chat_report_message")}</DialogTitle>
            <DialogDescription>{t("chat_report_message")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="chat-report-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
              </SelectContent>
            </Select>
            <Textarea
              data-testid="chat-report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={2000}
              placeholder=""
              className="min-h-[80px]"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportMsg(null)} className="rounded-full">
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              data-testid="chat-report-submit"
              onClick={submitReport}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white hover:text-white"
            >
              <Flag size={13} className="mr-1" /> {t("chat_report_message")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
